/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Theme Provider
 *
 * Applies organization branding colors to CSS variables at runtime.
 * Reads primaryColor and secondaryColor from organization settings and
 * dynamically updates CSS custom properties for theming.
 *
 * This enables:
 * - Organization-specific color theming (white labeling)
 * - Dynamic color palette generation
 * - Dark mode color adaptation
 */

"use client";

import { useOrganization } from "@/hooks/auth/use-organization";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { useThemeColors } from "./hooks/use-theme-colors";
import { useThemeEffects } from "./hooks/use-theme-effects";
import { readCachedOrgTheme } from "./utils/theme-storage";

/**
 * Theme Provider Component
 *
 * Applies organization branding colors to CSS variables.
 * Should be used within OrganizationProvider and UserPreferencesProvider.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { organization } = useOrganization();
  const settings = organization?.settings;
  const pathname = usePathname();
  const organizationId = organization?.organization_id;

  const cachedTheme = useMemo(() => {
    const cached = readCachedOrgTheme();
    if (!cached) return null;
    // Only reject the cache when we KNOW the org doesn't match.
    // When organizationId is still loading (null/undefined), trust the cache
    // so useLayoutEffect can apply it synchronously before paint — preventing
    // a flash of default colors on every page reload.
    if (organizationId && cached.orgId && cached.orgId !== organizationId) {
      return null;
    }
    return cached;
  }, [organizationId]);

  const { primaryColor, secondaryColor, organizationTheme, isMarketing } =
    useThemeColors({
      settings,
      cachedTheme,
      pathname,
    });

  useThemeEffects({
    primaryColor,
    secondaryColor,
    organizationTheme,
    isMarketing,
    organizationId,
    cachedTheme,
    settings: settings ?? null,
  });

  return <>{children}</>;
}
